import { useEffect, useState } from "react";
import { playSfx } from "../audio/sound";
import { IconAgencia } from "../ui/icons";
import styles from "./shell.module.css";

/**
 * Secuencia de boot (~4 s, os §1): crest de la Agencia → nombre del sistema →
 * "Montando expediente…" con barra de progreso de OS de época. Skippeable con
 * clic o tecla tras la primera vez.
 */

const PHASES = [
  { at: 0, key: "logo" },
  { at: 1400, key: "system" },
  { at: 2600, key: "mounting" },
  { at: 4000, key: "done" },
] as const;

type BootPhase = (typeof PHASES)[number]["key"];

interface Props {
  skippable: boolean;
  onDone: () => void;
}

export function Boot({ skippable, onDone }: Props) {
  const [phase, setPhase] = useState<BootPhase>("logo");

  useEffect(() => {
    playSfx("jingle"); // 2 s — el sonido de marca (D-20); silencioso si el navegador bloquea autoplay
    const timers = PHASES.filter((p) => p.at > 0).map((p) =>
      window.setTimeout(() => setPhase(p.key), p.at),
    );
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (phase === "done") onDone();
  }, [phase, onDone]);

  useEffect(() => {
    if (!skippable) return;
    const skip = () => onDone();
    window.addEventListener("pointerdown", skip);
    window.addEventListener("keydown", skip);
    return () => {
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("keydown", skip);
    };
  }, [skippable, onDone]);

  return (
    <div className={styles.bootScreen} data-testid="boot">
      <div className={styles.bootCrest} aria-hidden="true">
        <IconAgencia size={96} />
      </div>
      {(phase === "system" || phase === "mounting") && (
        <p className={styles.bootTitle}>
          ArgOS{" "}
          <span className={styles.bootSub}>Archive &amp; Records General Operating System</span>
        </p>
      )}
      <div className={styles.bootProgressTrack} aria-hidden="true">
        <div className={styles.bootProgressFill} />
      </div>
      {phase === "mounting" && (
        <p className={styles.bootMounting} data-testid="boot-mounting">
          Montando expediente…
        </p>
      )}
      {skippable && <p className={styles.bootSkipHint}>pulse para omitir</p>}
    </div>
  );
}
